import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, MapPin, Save, Loader2, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/lib/auth-context';
import { auth } from '@/lib/api';
import { getAuthToken, persistAuthSession } from '@/lib/auth-storage';
import { toast } from 'sonner';

const emptyAddress = {
  full_name: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
};

function parseUserAddress(user) {
  if (!user) return { ...emptyAddress };
  const raw = user.address;
  if (raw && typeof raw === 'object') {
    return {
      full_name: raw.full_name || user.name || '',
      phone: raw.phone || user.phone || '',
      address_line1: raw.address_line1 || '',
      address_line2: raw.address_line2 || '',
      city: raw.city || user.city || '',
      state: raw.state || '',
      postal_code: raw.postal_code || user.postal_code || '',
      country: raw.country || user.country || '',
    };
  }
  return {
    ...emptyAddress,
    full_name: user.name || '',
    phone: user.phone || '',
    address_line1: user.full_address || (typeof raw === 'string' ? raw : ''),
    city: user.city || '',
    postal_code: user.postal_code || '',
    country: user.country || '',
  };
}

const AccountSettingsPage = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState(emptyAddress);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
      setAddress(parseUserAddress(user));
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!address.full_name?.trim()) {
      toast.error(t('accountSettings.nameRequired'));
      return;
    }
    if (!address.phone?.trim()) {
      toast.error(t('accountSettings.phoneRequired'));
      return;
    }
    if (!address.address_line1?.trim() || !address.city?.trim() || !address.country?.trim()) {
      toast.error(t('accountSettings.addressRequired'));
      return;
    }

    try {
      setSaving(true);
      const res = await auth.updateProfile({
        name: name.trim(),
        phone: phone.trim() || address.phone.trim(),
        shipping_address: {
          ...address,
          full_name: address.full_name.trim(),
          phone: address.phone.trim(),
          address_line1: address.address_line1.trim(),
          city: address.city.trim(),
          country: address.country.trim(),
        },
      });
      setUser(res.data);
      const token = getAuthToken();
      if (token) {
        persistAuthSession(res.data, token, !!localStorage.getItem('token'));
      }
      toast.success(t('accountSettings.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-8" data-testid="account-settings-page">
      <div className="container mx-auto px-4 md:px-8 max-w-2xl">
        <div className="mb-6">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('accountSettings.backToAccount')}
          </Link>
          <h1 className="text-3xl md:text-4xl font-serif font-bold">{t('accountSettings.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('accountSettings.subtitle')}</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />
                {t('accountSettings.profile')}
              </CardTitle>
              <CardDescription>{t('accountSettings.profileDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="settings-name">{t('checkout.fullName')}</Label>
                <Input
                  id="settings-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  data-testid="settings-name"
                />
              </div>
              <div>
                <Label htmlFor="settings-email">{t('auth.email')}</Label>
                <Input id="settings-email" value={user?.email || ''} disabled className="mt-1 bg-muted" />
              </div>
              <div>
                <Label htmlFor="settings-phone">{t('checkout.mobilePhone')}</Label>
                <Input
                  id="settings-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1"
                  placeholder="+90 5XX XXX XX XX"
                  data-testid="settings-phone"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5" />
                {t('accountSettings.defaultAddress')}
              </CardTitle>
              <CardDescription>{t('accountSettings.defaultAddressDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="addr-full-name">{t('checkout.fullName')}</Label>
                <Input
                  id="addr-full-name"
                  value={address.full_name}
                  onChange={(e) => setAddress((p) => ({ ...p, full_name: e.target.value }))}
                  className="mt-1"
                  data-testid="settings-addr-name"
                />
              </div>
              <div>
                <Label htmlFor="addr-phone">{t('checkout.mobilePhone')}</Label>
                <Input
                  id="addr-phone"
                  value={address.phone}
                  onChange={(e) => setAddress((p) => ({ ...p, phone: e.target.value }))}
                  className="mt-1"
                  data-testid="settings-addr-phone"
                />
              </div>
              <div>
                <Label htmlFor="addr-line1">{t('checkout.addressLine1')}</Label>
                <Input
                  id="addr-line1"
                  value={address.address_line1}
                  onChange={(e) => setAddress((p) => ({ ...p, address_line1: e.target.value }))}
                  className="mt-1"
                  data-testid="settings-addr-line1"
                />
              </div>
              <div>
                <Label htmlFor="addr-line2">{t('checkout.addressLine2')}</Label>
                <Input
                  id="addr-line2"
                  value={address.address_line2}
                  onChange={(e) => setAddress((p) => ({ ...p, address_line2: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="addr-city">{t('checkout.city')}</Label>
                  <Input
                    id="addr-city"
                    value={address.city}
                    onChange={(e) => setAddress((p) => ({ ...p, city: e.target.value }))}
                    className="mt-1"
                    data-testid="settings-addr-city"
                  />
                </div>
                <div>
                  <Label htmlFor="addr-state">{t('checkout.state')}</Label>
                  <Input
                    id="addr-state"
                    value={address.state}
                    onChange={(e) => setAddress((p) => ({ ...p, state: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="addr-postal">{t('checkout.postalCode')}</Label>
                  <Input
                    id="addr-postal"
                    value={address.postal_code}
                    onChange={(e) => setAddress((p) => ({ ...p, postal_code: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="addr-country">{t('checkout.country')}</Label>
                  <Input
                    id="addr-country"
                    value={address.country}
                    onChange={(e) => setAddress((p) => ({ ...p, country: e.target.value }))}
                    className="mt-1"
                    placeholder="TR, DE, ..."
                    data-testid="settings-addr-country"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          <Button type="submit" className="w-full sm:w-auto rounded-full" disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('accountSettings.saving')}</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />{t('accountSettings.save')}</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
